import { ApiError, type UploadedFile } from './api';
import type { TranslationKey } from './i18n';

/**
 * My notes (operator, 2026-10-15): everyone's own to-do list and sticky notes,
 * which may tag — with @ — the tasks, Q Tasks and tickets given to them. A
 * note's words are formatted, and it may show pictures, since 2026-11-01
 * (Microsoft Sticky Notes' toolbar: "add this options to notes and add auto
 * save"). The shapes `GET /notes` and `GET /notes/taggable` hand out, and the
 * helpers the page, its note editor and its tag picker share.
 */

export type NoteTagKind = 'TASK' | 'Q_TASK' | 'TICKET';

/**
 * A tag as the API hands it out. While its owner can open the record it names:
 * the record's number (a ticket's or a Q Task's — a task has none), name, status
 * and priority, and whether it is still given to them. Once they cannot,
 * `available` is false and nothing more is said of it.
 */
export interface NoteTag {
  kind: NoteTagKind;
  id: string;
  available: boolean;
  number?: string | null;
  title?: string;
  status?: string;
  priority?: string | null;
  dueAt?: string | null;
  isLeaderTask?: boolean;
  mine?: boolean;
}

export interface TodoItem {
  id: string;
  title: string;
  isDone: boolean;
  doneAt: string | null;
  /** The day it is for, `YYYY-MM-DD`. */
  dueOn: string | null;
  /** Its place in the list, highest first. */
  position: number;
  createdAt: string;
  updatedAt: string;
  tags: NoteTag[];
}

export type StickyColor = 'AMBER' | 'CRIMSON' | 'CYAN' | 'GREEN' | 'VIOLET' | 'GRAPHITE';

export const STICKY_COLORS: StickyColor[] = ['AMBER', 'CRIMSON', 'CYAN', 'GREEN', 'VIOLET', 'GRAPHITE'];

/** A picture on a note: its owner's own upload, shown through its signed link. */
export type NoteImage = Pick<UploadedFile, 'id' | 'originalName' | 'mimeType' | 'sizeBytes' | 'url' | 'downloadUrl'>;

export interface StickyNoteItem {
  id: string;
  /**
   * Rich text: paragraphs, bold, italic, underline, strikethrough and bulleted
   * lists, as the API cleans it — '' when the note is tags or pictures alone.
   */
  body: string;
  color: StickyColor;
  isPinned: boolean;
  /** Its place on the board, highest first (pinned notes come first whatever their place). */
  position: number;
  /** When its words, tags or pictures last changed — not a pin, a colour or a drag. */
  editedAt: string | null;
  createdAt: string;
  updatedAt: string;
  tags: NoteTag[];
  /** Its pictures, in their order. */
  images: NoteImage[];
}

/** `GET /notes`: the to-do list (open lines in their order, then done ones) and the board (pinned first). */
export interface NotesOverview {
  todos: TodoItem[];
  notes: StickyNoteItem[];
}

/** `GET /notes/taggable`: what the @ picker offers — the open work given to the reader. */
export interface TaggableOptions {
  tasks: NoteTag[];
  qTasks: NoteTag[];
  tickets: NoteTag[];
}

/** The API's limits. */
export const TODO_TITLE_MAX = 300;
/** A note's words — the characters of its text, however they are formatted. */
export const NOTE_BODY_MAX = 2000;
export const MAX_TAGS = 10;
export const MAX_NOTE_IMAGES = 10;
/** The pictures a note takes: those every browser shows. */
export const NOTE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export const NOTES_KEY = ['notes'] as const;
export const TAGGABLE_KEY = ['notes', 'taggable'] as const;

/** Where a tag opens: a Leaders Task on its own page, as everywhere. */
export function tagLink(tag: Pick<NoteTag, 'kind' | 'id' | 'isLeaderTask'>): string {
  switch (tag.kind) {
    case 'TICKET':
      return `/tickets/${tag.id}`;
    case 'Q_TASK':
      return `/q-tasks/${tag.id}`;
    default:
      return tag.isLeaderTask ? `/leader-tasks/${tag.id}` : `/tasks/${tag.id}`;
  }
}

/** What a tag is, in a word: a task, a Leaders Task, a Q Task, a ticket. */
export function tagKindKey(tag: Pick<NoteTag, 'kind' | 'isLeaderTask'>): TranslationKey {
  switch (tag.kind) {
    case 'TICKET':
      return 'notes.tag.kind.TICKET';
    case 'Q_TASK':
      return 'notes.tag.kind.Q_TASK';
    default:
      return tag.isLeaderTask ? 'notes.tag.kind.LEADER_TASK' : 'notes.tag.kind.TASK';
  }
}

export const sameTag = (a: Pick<NoteTag, 'kind' | 'id'>, b: Pick<NoteTag, 'kind' | 'id'>) =>
  a.kind === b.kind && a.id === b.id;

/** The tags as the API takes them: one list of ids per kind. */
export function tagIdLists(tags: NoteTag[]) {
  const of = (kind: NoteTagKind) => tags.filter((tag) => tag.kind === kind).map((tag) => tag.id);
  return { taskIds: of('TASK'), qTaskIds: of('Q_TASK'), ticketIds: of('TICKET') };
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Today — or another moment's day — as the reader's own calendar has it, `YYYY-MM-DD`. */
export function localDay(date: Date = new Date()): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** A `YYYY-MM-DD` as a moment on that day where the reader is, to format: never a day early west of UTC. */
export function dayDate(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Where a line's day stands against today. */
export function dueState(day: string, today: string = localDay()): 'overdue' | 'today' | 'tomorrow' | 'later' {
  if (day < today) return 'overdue';
  if (day === today) return 'today';
  const next = new Date(dayDate(today));
  next.setDate(next.getDate() + 1);
  return day === localDay(next) ? 'tomorrow' : 'later';
}

/**
 * What the API refused, in the reader's language when it is one of the refusals
 * the page knows — else the API's own words.
 */
export function noteRefusal(error: unknown, t: (key: TranslationKey, vars?: Record<string, string | number>) => string): string {
  if (error instanceof ApiError) {
    const details = (error.details ?? {}) as { reason?: string; max?: number; field?: string };
    switch (details.reason) {
      case 'EMPTY':
        return t(details.field === 'body' ? 'notes.problem.EMPTY_NOTE' : 'notes.problem.EMPTY');
      case 'NOT_AVAILABLE':
        return t('notes.problem.NOT_AVAILABLE');
      case 'TOO_MANY_TAGS':
        return t('notes.tag.max', { n: details.max ?? MAX_TAGS });
      case 'TOO_LONG':
        return t('notes.problem.TOO_LONG', { n: details.max ?? NOTE_BODY_MAX });
      case 'TOO_MANY_IMAGES':
        return t('notes.problem.TOO_MANY_IMAGES', { n: details.max ?? MAX_NOTE_IMAGES });
      case 'NOT_AN_IMAGE':
        return t('notes.problem.NOT_AN_IMAGE');
      case 'NOT_A_DAY':
        return t('notes.problem.NOT_A_DAY');
      case 'FULL':
        return error.message;
    }
    if (error.code === 'FILE_NOT_ATTACHABLE') return t('notes.problem.NOT_ATTACHABLE');
    if (error.code === 'CONCURRENT_CHANGE') return t('notes.reloaded');
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * A note's words as plain lines — for what names it to a screen reader, never
 * to draw it. The browser reads the API's rich text without running or loading
 * anything in it: a parsed document has no window.
 */
export function noteLines(html: string): string[] {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const blocks = doc.body.querySelectorAll('p, li');
  const lines = blocks.length
    ? Array.from(blocks)
        // A list item's own paragraph would say it twice.
        .filter((block) => !(block.tagName === 'LI' && block.querySelector('p')))
        .map((block) => block.textContent ?? '')
    : [doc.body.textContent ?? ''];
  return lines.map((line) => line.trim()).filter(Boolean);
}
